// BB FILE: BB custom script: keeps the product detail list (ISBN, SKU, format, pages, dimensions) and the
// variant note (incl. POD estimated dispatch date) in sync with the selected variant.
// Loaded as a module from the footer-group custom-liquid section (editor data); the bare '@shopify/events'
// specifier resolves through the theme import map in snippets/scripts.liquid.
// BB START: Atelier 4.x event wiring. 4.0 removed ThemeEvents.variantUpdate / VariantUpdateEvent; a variant change
// now dispatches the Storefront standard event 'shopify:product:select' (StandardEvents.productSelect) from the
// variant picker (assets/variant-picker.js fetchUpdatedSection). The event fires BEFORE the fetch; its .promise
// resolves afterwards with { detail: { html, productId, newProduct, sourceId, resource } } (resource = selected
// variant JSON, or null) and rejects with an AbortError when a newer selection cancels the fetch.
import { StandardEvents } from '@shopify/events';

/**
 * BB: subscribe `element` to product:select events on `target` (its section or dialog). `callback(detail)` runs once
 * the section fetch resolves, only for the product the element belongs to (data-product-id) and never for a
 * product-card swatch elsewhere in the same section. Returns an AbortController for disconnectedCallback.
 *
 * Mirrors when the 3.5 VariantUpdateEvent fired: only when the re-rendered variant picker carried its variant JSON
 * script (4.2 also resolves when it is missing - skipped here). The JSON may be "null" (unavailable combination):
 * 3.5 still fired then with resource = null, so a caller that needs the variant passes { requireResource: true }.
 * @param {HTMLElement} element
 * @param {EventTarget} target
 * @param {(detail: any) => void} callback
 * @param {{ requireResource?: boolean }} [options]
 */
function bbOnProductSelect(element, target, callback, { requireResource = false } = {}) {
  const controller = new AbortController();
  target.addEventListener(
    StandardEvents.productSelect,
    (event) => {
      if (!(event.target instanceof Element)) return;
      const sourceCard = event.target.closest('product-card');
      if (sourceCard && !sourceCard.contains(element)) return;
      if (!event.promise) return;

      event.promise
        .then((result) => {
          const detail = result?.detail;
          if (!detail?.html) return;
          if (!detail.html.querySelector('variant-picker script[type="application/json"]')?.textContent) return;
          if (requireResource && !detail.resource) return;

          if (detail.newProduct) {
            element.dataset.productId = detail.newProduct.id;
          } else if (detail.productId && element.dataset.productId && detail.productId !== element.dataset.productId) {
            return;
          }

          callback(detail);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[bb-product-variants] Event promise rejected:', error);
        });
    },
    { signal: controller.signal }
  );
  return controller;
}
// BB END: Atelier 4.x event wiring

if (!customElements.get('bb-product-detail-list')) {
  customElements.define('bb-product-detail-list', class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      const closestSection = this.closest('.shopify-section, dialog');
      if (!closestSection) return;
      // BB: 4.x product:select subscription (was closestSection.addEventListener(ThemeEvents.variantUpdate, ...))
      this.bbEventController?.abort();
      // (3.5 threw on a null variant before fetching, so the list only updates for a real variant)
      this.bbEventController = bbOnProductSelect(
        this,
        closestSection,
        (detail) => this.UpdateProductDetailInfo({ detail }),
        { requireResource: true }
      );
    }

    // BB: remove the product:select listener when the element leaves the page (e.g. quick-add dialog re-render)
    disconnectedCallback() {
      this.bbEventController?.abort();
      this.bbEventController = undefined;
    }

    async UpdateProductDetailInfo(e) {
      // console.log(e, e.detail.resource);
      let variant = e.detail.resource;
      // console.log(variant);
      let url = `${this.dataset.product_url}?section_id=${this.dataset.section_id}&variant=${variant.id}`;
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error('Network response was not ok');
        const res_text = await res.text();
        // console.log(res_text);
        const parser = new DOMParser();
        const doc = parser.parseFromString(res_text, 'text/html');
        const new_content = doc.querySelector('bb-product-detail-list');
        // console.log(new_content);
        if (new_content) {
          this.innerHTML = new_content.innerHTML;
        }
      }
      catch (error) {
        console.error('Error fetching product details:', error);
      }
    }
  });
}

if (!customElements.get('variant-note')) {
  class VariantNote extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      let id = this.dataset.id || '';
      this.id = id;
      let $group = this.closest('.group-block');
      this.$group = $group;

      // BB START: swap a POD print time for an estimated dispatch date (u_BB_FillPodDispatchNotes,
      // _bb-custom.js). On first page load that script isn't loaded yet and fills every note itself;
      // this call covers notes added later (e.g. a quick-add dialog).
      if (typeof u_BB_FillPodDispatchNotes === 'function') u_BB_FillPodDispatchNotes(this);
      // BB END: POD estimated dispatch

      const closestSection = this.closest('.shopify-section, dialog');
      if (!closestSection) return;
      // BB: 4.x product:select subscription (was closestSection.addEventListener(ThemeEvents.variantUpdate, ...));
      // detail.html is the same re-rendered section the 3.5 event carried as e.detail.data.html
      this.bbEventController?.abort();
      this.bbEventController = bbOnProductSelect(this, closestSection, (detail) =>
        this.UpdateNote({ detail: { data: { html: detail.html } } })
      );
    }

    // BB: remove the product:select listener when the element leaves the page (e.g. quick-add dialog re-render)
    disconnectedCallback() {
      this.bbEventController?.abort();
      this.bbEventController = undefined;
    }

    async UpdateNote(e) {
      // console.log(e.detail);
      if (!(e.detail.data.html instanceof Document || e.detail.data.html instanceof Element)) return;
      const $new_variant_note = e.detail.data.html.querySelector(`variant-note[data-id="${this.id}"]`);
      // console.log(this.$group, $new_group);
      if ($new_variant_note) {
        this.innerHTML = $new_variant_note.innerHTML;
        // BB: the fresh HTML has the note's own print-time text again — re-apply the dispatch date
        if (typeof u_BB_FillPodDispatchNotes === 'function') u_BB_FillPodDispatchNotes(this);
      }
    }
  }
  customElements.define('variant-note', VariantNote);
}
